import { equipmentName } from '../data/equipment';
import type { Exercise } from '../data/exercise-types';
import { getExercise } from '../data/exercises';
import { MUSCLES } from '../data/muscles';
import { exerciseHistory, personalRecord } from '../engine/progression';
import { formatRelative } from '../lib/dates';
import { useApp } from '../store/AppStore';
import { LineChart } from './Chart';
import { ExerciseFrames } from './ExerciseImages';
import { IconStar } from './Icons';

export function ExerciseDetail({
  exercise,
  onPick,
}: {
  exercise: Exercise;
  /** When provided, variations become tappable to swap to them. */
  onPick?: (id: string) => void;
}) {
  const { state, toggleFavorite, toggleExcluded } = useApp();
  const pr = personalRecord(state.workouts, exercise.id);
  const history = exerciseHistory(state.workouts, exercise.id);
  const isFavorite = state.exercisePrefs.favorites.includes(exercise.id);
  const isExcluded = state.exercisePrefs.excluded.includes(exercise.id);
  const available = new Set(state.settings.equipment);

  return (
    <div className="stack">
      {state.settings.showImages && <ExerciseFrames exerciseId={exercise.id} />}

      {exercise.aka && exercise.aka.length > 0 && (
        <div className="tiny faint">Also called: {exercise.aka.join(', ')}</div>
      )}

      <div className="row wrap" style={{ gap: 6 }}>
        {exercise.primary.map((m) => (
          <span className="pill accent" key={m}>
            {MUSCLES[m].short}
          </span>
        ))}
        {exercise.secondary.map((m) => (
          <span className="pill" key={m}>
            {MUSCLES[m].short}
          </span>
        ))}
      </div>

      <div className="grid-3">
        <div className="stat">
          <div className="label">Sets × reps</div>
          <div className="value" style={{ fontSize: 16 }}>
            {exercise.defaultSets} × {exercise.repRange[0]}–{exercise.repRange[1]}
          </div>
        </div>
        <div className="stat">
          <div className="label">Rest</div>
          <div className="value" style={{ fontSize: 16 }}>
            {Math.round(exercise.restSeconds / 60)}
            <span className="unit"> min</span>
          </div>
        </div>
        <div className="stat">
          <div className="label">Level</div>
          <div className="value" style={{ fontSize: 16 }}>
            {['Easy', 'Moderate', 'Technical'][exercise.difficulty - 1]}
          </div>
        </div>
      </div>

      <div>
        <div className="card-title">Set up</div>
        <ol style={{ margin: 0, paddingLeft: 18 }} className="small muted">
          {exercise.setup.map((step, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {step}
            </li>
          ))}
        </ol>
      </div>

      <div>
        <div className="card-title">How to do it</div>
        <ul style={{ margin: 0, paddingLeft: 18 }} className="small">
          {exercise.cues.map((cue, i) => (
            <li key={i} style={{ marginBottom: 5 }}>
              {cue}
            </li>
          ))}
        </ul>
      </div>

      {exercise.mistakes && exercise.mistakes.length > 0 && (
        <div>
          <div className="card-title">Common mistakes</div>
          <ul style={{ margin: 0, paddingLeft: 18 }} className="small muted">
            {exercise.mistakes.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="card-title">Equipment</div>
        <div className="row wrap" style={{ gap: 6 }}>
          {exercise.equipment.map((id) => (
            <span className={`pill${available.has(id) ? '' : ' danger'}`} key={id}>
              {equipmentName(id)}
              {available.has(id) ? '' : ' — not available'}
            </span>
          ))}
        </div>
      </div>

      {exercise.variations && exercise.variations.length > 0 && (
        <div>
          <div className="card-title">Variations</div>
          <div className="stack" style={{ gap: 6 }}>
            {exercise.variations.map((id) => {
              const v = getExercise(id);
              if (!v) return null;
              const canDo = v.equipment.every((eq) => available.has(eq));
              return (
                <button
                  key={id}
                  className="btn ghost"
                  style={{ justifyContent: 'space-between', opacity: canDo ? 1 : 0.5 }}
                  disabled={!onPick || !canDo}
                  onClick={() => onPick?.(id)}
                >
                  <span style={{ color: 'var(--text)' }}>{v.name}</span>
                  <span className="tiny faint">
                    {canDo ? (onPick ? 'switch to this' : '') : 'no equipment'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {pr && (
        <div>
          <div className="card-title">Your best</div>
          <div className="grid-2">
            <div className="stat">
              <div className="label">Top set</div>
              <div className="value">
                {pr.bestWeight}
                <span className="unit"> kg × {pr.bestWeightReps}</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">Est. 1RM</div>
              <div className="value">
                {pr.best1rm}
                <span className="unit"> kg</span>
              </div>
            </div>
          </div>
          <div className="tiny faint" style={{ marginTop: 6 }}>
            Set {formatRelative(pr.date)}.
          </div>
        </div>
      )}

      {history.length >= 2 && (
        <div>
          <div className="card-title">Strength trend</div>
          <LineChart
            points={history.slice(-16).map((p) => ({
              label: formatRelative(p.date),
              value: p.e1rm,
            }))}
            unit=" kg"
          />
          <div className="tiny faint" style={{ marginTop: 8 }}>
            Estimated 1RM from your best set each session. It combines weight and
            reps, so a lighter set for more reps still shows as progress instead
            of looking like a step backwards.
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="card-title">Recent sessions</div>
          <div className="stack" style={{ gap: 5 }}>
            {history
              .slice(-6)
              .reverse()
              .map((point) => (
                <div className="row between" key={point.date}>
                  <span className="small muted">{formatRelative(point.date)}</span>
                  <span className="tiny mono faint">
                    {point.topWeightKg} kg × {point.topReps} · {point.sets} sets
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="divider" />

      <div className="row" style={{ gap: 8 }}>
        <button
          className={`btn grow${isFavorite ? ' primary' : ' ghost'}`}
          onClick={() => toggleFavorite(exercise.id)}
        >
          <IconStar filled={isFavorite} />
          {isFavorite ? 'Favourite' : 'Favourite'}
        </button>
        <button
          className={`btn grow${isExcluded ? ' danger' : ' ghost'}`}
          onClick={() => toggleExcluded(exercise.id)}
        >
          {isExcluded ? 'Excluded — tap to allow' : 'Never suggest this'}
        </button>
      </div>
    </div>
  );
}
